import type { CaseReason, TradeRole } from 'types/interfaces/thread';

/**
 * Case reasons (Asana 1219952262403623). The long labels and helpers are the
 * ticket's final copy for the Summon Admin wizard; the short labels are what
 * chips, rail facts and the admin queue show. Legacy cases carry reason null.
 */

export const CASE_REASON_SHORT_LABELS: Record<CaseReason, string> = {
  SENDER_PARTNER_NOT_COMPLETED: 'Partner has not completed',
  SENDER_CANCEL: 'Cancel request',
  RECEIVER_NOT_ARRIVED: 'Not arrived',
  RECEIVER_ARRIVED_INCORRECT: 'Arrived incorrect',
  RECEIVER_CANCEL: 'Cancel request',
  OTHER: 'Other',
};

export const NO_REASON_LABEL = 'No reason given';

export const caseReasonShortLabel = (reason: CaseReason | null | undefined): string =>
  reason ? CASE_REASON_SHORT_LABELS[reason] : NO_REASON_LABEL;

export interface CaseReasonOption {
  value: CaseReason;
  label: string;
  helper: string;
}

/** Final copy, verbatim from the ticket. Order is the order shown. */
export const SENDER_REASONS: CaseReasonOption[] = [
  {
    value: 'SENDER_PARTNER_NOT_COMPLETED',
    label: "My trading partner hasn't completed the trade",
    helper: "The package was delivered but they haven't marked it complete.",
  },
  {
    value: 'SENDER_CANCEL',
    label: 'I need to cancel this trade',
    helper: "Something came up, or the card isn't available anymore.",
  },
  { value: 'OTHER', label: 'Other', helper: 'Something else about this trade.' },
];

export const RECEIVER_REASONS: CaseReasonOption[] = [
  {
    value: 'RECEIVER_NOT_ARRIVED',
    label: "My trade hasn't arrived",
    helper: "It shows shipped but nothing has turned up.",
  },
  {
    value: 'RECEIVER_ARRIVED_INCORRECT',
    label: 'My trade arrived incorrect',
    helper: 'Wrong card, condition, language, finish, or something missing.',
  },
  {
    value: 'RECEIVER_CANCEL',
    label: "I'd like to cancel this trade",
    helper: "You no longer want these cards, or they haven't shipped.",
  },
  { value: 'OTHER', label: 'Other', helper: 'Something else about this trade.' },
];

export const reasonsForRole = (role: TradeRole): CaseReasonOption[] =>
  role === 'SENDER' ? SENDER_REASONS : RECEIVER_REASONS;

export const caseReasonLongLabel = (reason: CaseReason): string =>
  [...SENDER_REASONS, ...RECEIVER_REASONS].find((r) => r.value === reason)?.label ?? CASE_REASON_SHORT_LABELS[reason];

export const CANCEL_NOTE: Record<TradeRole, string> = {
  SENDER:
    "If you haven't shipped yet, an admin can cancel the trade and return the points to both of you. If you have shipped, say so below.",
  RECEIVER:
    'An admin will check whether the cards have shipped and work out the cancellation with you and the sender.',
};

export const roleLabel = (role: TradeRole): string => (role === 'SENDER' ? 'sender' : 'receiver');
